
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { usePricingPlans } from "@/components/landing/pricing/usePricingPlans";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";
import { useState } from "react";
import { toast } from "sonner";

interface PricingSheetProps {
  onClose?: () => void;
}

export function PricingSheet({ onClose }: PricingSheetProps) {
  const { 
    isAnnual, 
    setIsAnnual, 
    calculatePrice, 
    getAnnualSavings, 
    getAnnualTotal,
    getPricePerReport,
    plans
  } = usePricingPlans();
  
  const { startCheckout, loading } = useStripeCheckout();
  const [processingPlan, setProcessingPlan] = useState<string | null>(null);

  const handlePlanSelection = async (planName: string) => {
    try {
      console.log('Selected plan:', planName);
      setProcessingPlan(planName);
      
      // Find the selected plan from plans array
      const selectedPlan = plans.find(plan => plan.name.toLowerCase() === planName.toLowerCase());
      
      if (!selectedPlan) {
        console.error(`Plan ${planName} not found in plans array`);
        toast.error("Could not find the selected plan. Please try again.");
        setProcessingPlan(null);
        return;
      }
      
      // Get the price ID based on billing cycle
      const priceId = isAnnual 
        ? selectedPlan.priceId?.annual 
        : selectedPlan.priceId?.monthly;
      
      if (!priceId) {
        console.error(`Price ID not found for plan ${planName}`);
        toast.error("Price information not available. Please try again.");
        setProcessingPlan(null);
        return;
      }
      
      console.log(`Starting checkout for ${planName} with price ID: ${priceId}`);
      await startCheckout(priceId, isAnnual ? 'annual' : 'monthly');
      
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error("Error during checkout:", error);
      toast.error("Failed to start checkout. Please try again.");
    } finally {
      setProcessingPlan(null);
    }
  };

  return (
    <DialogContent className="max-w-[720px] bg-gray-900 border border-white/20 rounded-2xl p-4 sm:p-5">
      <DialogHeader className="space-y-3 mb-4">
        <DialogTitle className="text-white text-center text-xl">Subscription Required</DialogTitle>
        <div className="flex flex-col items-center gap-2">
          <p className="text-gray-300 text-center text-sm mb-2">
            Select a plan below to generate reports.
          </p>
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => setIsAnnual(false)}
              className={`text-xs ${!isAnnual ? 'text-white font-semibold border-b-2 border-[#1EAEDB]' : 'text-gray-400 hover:text-gray-300'}`}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsAnnual(true)}
              className={`text-xs ${isAnnual ? 'text-white font-semibold border-b-2 border-[#1EAEDB]' : 'text-gray-400 hover:text-gray-300'}`}
            >
              Annual
            </button>
          </div>
        </div>
      </DialogHeader>

      <div className="text-center mb-3">
        {!isAnnual && (
          <span className="text-xs text-[#1EAEDB] font-medium">Save 15% with annual billing</span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Starter Plan */}
        <div className="border border-[#1EAEDB]/20 rounded-2xl p-3 bg-gray-800/90 flex flex-col h-full">
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-semibold text-white">Starter Plan</h3>
              <span className="text-[#1EAEDB]">📈</span>
            </div>
            <p className="text-xs text-gray-400">Regular research needs</p>
          </div>
          <div className="mb-3">
            <div className="flex items-end gap-1 mb-1">
              <span className="text-xl font-bold text-white">${calculatePrice(7.99)}</span>
              <span className="text-xs text-gray-400">/month</span>
            </div>
            <div className="text-xs text-gray-400">
              {getPricePerReport(calculatePrice(7.99), 10)}
            </div>
            {isAnnual && (
              <div className="space-y-1 text-xs mt-1">
                <p className="text-gray-400">Annual total: <span className="text-white font-medium">${getAnnualTotal(7.99)}</span></p>
                <p className="text-green-400">You save: ${getAnnualSavings(7.99)}/year</p>
              </div>
            )}
          </div>
          <Button 
            className="w-full mb-3 text-xs py-1.5 bg-[#1EAEDB] hover:bg-[#1EAEDB]/90"
            onClick={() => handlePlanSelection('starter')}
            disabled={loading || processingPlan === 'starter'}
          >
            {processingPlan === 'starter' ? "Processing..." : "Get Started"}
          </Button>
          <ul className="space-y-2 mt-auto text-xs">
            <Feature>10 Reports per month</Feature>
            <Feature>PDF Export</Feature>
            <Feature>30-day report history</Feature>
            <Feature>Email Support</Feature>
          </ul>
        </div>

        {/* Pro Plan */}
        <div className="border border-[#1EAEDB]/40 rounded-2xl p-3 bg-gray-800/90 flex flex-col h-full relative">
          <div className="absolute -top-2 right-2 bg-[#1EAEDB] text-white text-xs px-2 py-0.5 rounded-full">
            Popular
          </div>
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-semibold text-white">Pro Plan</h3>
              <span className="text-[#1EAEDB]">🏆</span>
            </div>
            <p className="text-xs text-gray-400">Power users and teams</p>
          </div>
          <div className="mb-3">
            <div className="flex items-end gap-1 mb-1">
              <span className="text-xl font-bold text-white">${calculatePrice(17.99)}</span>
              <span className="text-xs text-gray-400">/month</span>
            </div>
            <div className="text-xs text-gray-400">
              {getPricePerReport(calculatePrice(17.99), 30)}
            </div>
            {isAnnual && (
              <div className="space-y-1 text-xs mt-1">
                <p className="text-gray-400">Annual total: <span className="text-white font-medium">${getAnnualTotal(17.99)}</span></p>
                <p className="text-green-400">You save: ${getAnnualSavings(17.99)}/year</p>
              </div>
            )}
          </div>
          <Button 
            className="w-full mb-3 text-xs py-1.5 bg-[#1EAEDB] hover:bg-[#1EAEDB]/90"
            onClick={() => handlePlanSelection('pro')}
            disabled={loading || processingPlan === 'pro'}
          >
            {processingPlan === 'pro' ? "Processing..." : "Get Started"}
          </Button>
          <ul className="space-y-2 mt-auto text-xs">
            <Feature>30 Reports per month</Feature>
            <Feature>PDF Export</Feature>
            <Feature>Unlimited report history</Feature>
            <Feature>Priority Support</Feature>
          </ul>
        </div>

        {/* Premium Plan */}
        <div className="border border-[#1EAEDB]/20 rounded-2xl p-3 bg-gray-800/90 flex flex-col h-full relative overflow-hidden">
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-semibold text-white">Premium Plan</h3>
              <span className="text-[#1EAEDB]">🌟</span>
            </div>
            <p className="text-xs text-gray-400">For organizations</p>
          </div>
          <div className="mb-3">
            <div className="flex items-end gap-1 mb-1">
              <span className="text-xl font-bold text-white">${calculatePrice(29.99)}</span>
              <span className="text-xs text-gray-400">/month</span>
            </div>
            <div className="text-xs text-gray-400">
              {getPricePerReport(calculatePrice(29.99), 60)}
            </div>
            {isAnnual && (
              <div className="space-y-1 text-xs mt-1">
                <p className="text-gray-400">Annual total: <span className="text-white font-medium">${getAnnualTotal(29.99)}</span></p>
                <p className="text-green-400">You save: ${getAnnualSavings(29.99)}/year</p>
              </div>
            )}
          </div>
          <Button 
            className="w-full mb-3 text-xs py-1.5 bg-[#1EAEDB] hover:bg-[#1EAEDB]/90"
            onClick={() => handlePlanSelection('premium')}
            disabled={loading || processingPlan === 'premium'}
          >
            {processingPlan === 'premium' ? "Processing..." : "Get Started"}
          </Button>
          <ul className="space-y-2 mt-auto text-xs">
            <Feature>60 Reports per month</Feature>
            <Feature>PDF Export</Feature>
            <Feature>Unlimited report history</Feature>
            <Feature>Priority Support</Feature>
          </ul>
        </div>
      </div>

      <div className="mt-3 text-center">
        <p className="text-xs text-gray-400">
          Need a custom plan?{" "}
          <a href="/contact" className="text-[#1EAEDB] hover:text-[#1EAEDB]/80">
            Contact us →
          </a>
        </p>
      </div>
    </DialogContent>
  );
}

function Feature({ children, isGreen = false }: { children: React.ReactNode; isGreen?: boolean }) {
  return (
    <li className="flex items-center gap-2 text-gray-300">
      <Check className="h-3 w-3 text-green-500 flex-shrink-0" />
      <span className={isGreen ? "text-green-400 font-medium" : ""}>{children}</span>
    </li>
  );
}
